/**
 * Формат содержимого сообщения.
 *
 * Внутри шифротекста лежит не голый текст, а маленький JSON. Это позволяет
 * положить туда ссылку на цитируемое сообщение так, чтобы сервер её не видел:
 * иначе пришлось бы завести столбец reply_to, и он выдавал бы структуру
 * разговора — кто кому отвечает — тому, кто не должен знать даже этого.
 *
 * Подключается и в Service Worker, поэтому без зависимостей.
 */

export interface ReplyReference {
  /** Идентификатор цитируемого сообщения. */
  id: string;
  /** Имя автора на момент цитирования — чтобы цитата читалась и без загрузки оригинала. */
  author: string;
  /** Короткая выдержка. Полный текст оригинала берётся из локальной истории. */
  preview: string;
}

export interface MessageBody {
  text: string;
  replyTo?: ReplyReference;
}

/* Обрезка цитаты: в пузырь всё равно не влезет больше, а шифротекст растёт. */
const PREVIEW_LIMIT = 120;

export function encodeBody(body: MessageBody): string {
  const payload: Record<string, unknown> = { v: 1, t: body.text };

  if (body.replyTo) {
    payload.r = {
      i: body.replyTo.id,
      a: body.replyTo.author.slice(0, 64),
      p: body.replyTo.preview.slice(0, PREVIEW_LIMIT),
    };
  }
  return JSON.stringify(payload);
}

/**
 * Разбор содержимого.
 *
 * Сообщения, отправленные до появления цитат, — просто строки, поэтому всё,
 * что не разбирается как наш JSON, считается обычным текстом. Без этого вся
 * прежняя переписка отобразилась бы кашей.
 */
export function decodeBody(plaintext: string): MessageBody {
  if (!plaintext.startsWith('{')) return { text: plaintext };

  try {
    const parsed = JSON.parse(plaintext) as {
      v?: number;
      t?: string;
      r?: { i?: string; a?: string; p?: string };
    };
    if (parsed.v !== 1 || typeof parsed.t !== 'string') return { text: plaintext };

    const reply =
      parsed.r && typeof parsed.r.i === 'string'
        ? {
            id: parsed.r.i,
            author: typeof parsed.r.a === 'string' ? parsed.r.a : '',
            preview: typeof parsed.r.p === 'string' ? parsed.r.p : '',
          }
        : undefined;

    return reply ? { text: parsed.t, replyTo: reply } : { text: parsed.t };
  } catch {
    return { text: plaintext };
  }
}

/** Однострочная выжимка для списка чатов и уведомлений. */
export function previewOf(plaintext: string): string {
  const body = decodeBody(plaintext);
  const text = body.text.replace(/\s+/g, ' ').trim();
  return body.replyTo ? `↩ ${text}` : text;
}
